import { format } from "date-fns";
import { CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import { useEffect, useId, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  CheckboxInput,
  DateInput,
  DropdownCheckbox,
  DropdownSelect,
  Icon,
  Modal,
  PhoneNumberInput,
  TextArea,
  TextInput,
  Tooltip,
  Typography,
} from "@/components/atoms";
import { charactersLeftLabel } from "@/components/atoms/TextArea/TextArea";
import { LocationAddressAutocomplete } from "@/components/organisms/MyFavourites/FavouriteLocationsTable/components/LocationAddressAutocomplete";
import { isFlightNumberEnabled } from "@/helpers/bookingHelpers";
import { ClientBookingData } from "@/redux/apis/clientBooking/types";
import { useAppSelector } from "@/redux/hooks";
import { defaultCountrySelector } from "@/redux/slices/operator/selectors";
import { BulkRowError } from "@/redux/apis/clientBooking/types";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
// The booking sheet's own grid, imported rather than restated — the whole point of that module is that
// bulk and the manual flow can't drift apart on pickup rules.
import { STEP_MINUTES } from "@/components/pages/Client/booking/pickupTime";
import { TITLE_OPTIONS } from "@/components/organisms/MyFavourites/GuestPassengersTable/guestForm";
import { clsx } from "@/utils";
import { BOOKING_FIELDS, BookingField, BookingFieldKey } from "../bookingFields";
import { DATE_FORMAT, parsePickupDate, parsePickupTime, toTwelveHour } from "../pickupFormat";

/**
 * The fields, grouped and laid out a row at a time.
 *
 * Two groups with the booking sheet's own names and icons — Address (Location) and Trip Details (User)
 * — because they are the sheet's groups. Fourteen fields in one flat column read as a form to fill in;
 * the same fourteen under the headings the user already met in the wizard read as the booking they are
 * correcting.
 *
 * Rows are explicit rather than derived from BOOKING_FIELDS, which paired them by accident: Passenger
 * ended up beside Notes to Driver, and Pickup Time beside Transfer Type.
 */
const SECTIONS: { icon: string; title: string; rows: BookingFieldKey[][] }[] = [
  {
    icon: "Location",
    title: "Address",
    rows: [["pickup"], ["destination"]],
  },
  {
    icon: "User",
    title: "Trip Details",
    rows: [
      ["passengerTitle"],
      ["passengerFirstName", "passengerLastName"],
      ["passengerEmail", "passengerPhone"],
      ["pickupDate", "pickupTime"],
      ["flightNumber", "arrivalDelay"],
      ["transferType", "vehicleType"],
      // Extras before the counts, matching the sheet's Vehicle and Transfer step: the vehicle, then
      // what goes in it, then how many people and bags.
      ["extras"],
      ["passengerCount", "bagCount"],
      ["notesToDriver"],
    ],
  },
];

/**
 * Notes cap, which the TextArea both enforces and counts down from.
 *
 * 255 to match the booking sheet's own `additionalInfo` rule and BulkBookingRowService::MAX_NOTES. The
 * server refuses a longer note, so letting one be typed here only defers the rejection to the save.
 */
const NOTES_MAX_LENGTH = 255;

/** Minutes are entered in the same 12-hour + meridiem shape the booking sheet's Date/Time step uses. */
const MERIDIEMS = ["AM", "PM"];

const normalise = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const canonicalOrUndefined = (raw: string, options: string[]) => options.find((option) => normalise(option) === normalise(raw));

/** The operator's spelling of a raw cell value, or the raw value when nothing matches. */
export const canonical = (raw: string, options: string[]) => canonicalOrUndefined(raw, options) ?? raw;

/**
 * The operator's extras named in a raw comma-separated cell, deduped.
 *
 * Commas separate extras, but an extra's own NAME can contain one — "Forward facing baby seat,
 * 9 months to 3 years" is a single option. Splitting on commas alone tore it into two fragments that
 * matched nothing, so it rendered unticked and ticking it silently did nothing: the write joined it
 * back with a comma and the next parse tore it apart again.
 *
 * So commas are treated as CANDIDATE boundaries. Tokens are recombined longest-run-first, which lets
 * a name containing a comma beat its own prefix, and degrades to a plain split when no option has
 * one. Whatever matches nothing is dropped — no checkbox represents it, so it could never be
 * unticked, and the server already warns it won't be added.
 *
 * Exported for test: this decides which boxes render ticked, and mirrors the server's own matching.
 */
export const matchExtras = (raw: string, options: string[]) => {
  const tokens = raw
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);

  const selected: string[] = [];

  for (let index = 0; index < tokens.length; ) {
    let matched: string | undefined;
    let consumed = 1;

    for (let run = tokens.length - index; run >= 1; run--) {
      const hit = canonicalOrUndefined(tokens.slice(index, index + run).join(", "), options);
      if (hit) {
        matched = hit;
        consumed = run;
        break;
      }
    }

    if (matched) selected.push(matched);
    index += consumed;
  }

  return [...new Set(selected)];
};

/**
 * One titled group of fields.
 *
 * Its own shell, not the booking summary's `SummaryBox`, which this used to borrow. The two look
 * alike and mean different things: SummaryBox DISPLAYS a step, and its tone tracks whether that step
 * is active, filled or still empty — none of which applies to a heading sitting over a set of inputs.
 * Sharing it meant a display component grew props it had no use for so that a form could size its
 * headings, and put the booking sheet's summary panel in the blast radius of an edit-modal tweak.
 *
 * The icon and title stay the sheet's own — same names, same `lg` size — so the editor still reads as
 * part of the same product. Only the heading size and the 6px gap are this modal's.
 */
const Section = ({ icon, title, children }: React.PropsWithChildren<{ icon: string; title: string }>) => (
  <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-3">
    <div className="flex items-center gap-1.5">
      <Icon name={icon} size="lg" className="text-neutral-dark-gray" />
      <Typography variant="h2" className="text-neutral-dark-gray">
        {title}
      </Typography>
    </div>
    <div className="mt-3">{children}</div>
  </div>
);

const fieldOf = (key: BookingFieldKey) => BOOKING_FIELDS.find((field) => field.key === key)!;

/**
 * Cap a value quoted back inside an error message.
 *
 * A mis-mapped column puts anything in any field — a row where Passenger Phone had been mapped to a
 * link quoted the whole URL back, three lines of it, under a phone input.
 */
const truncate = (value: string, max = 32) => (value.length > max ? `${value.slice(0, max)}…` : value);

/**
 * Label + inline error around whichever control the field uses.
 *
 * Declared at module scope, and it has to stay there. Defined inside EditRowModal it would be a new
 * function identity on every render, so React would see a different component type each keystroke
 * and remount the whole subtree — destroying the input, its focus and its caret. The address
 * autocompletes suffered worst, since remounting also wiped their query and closed their dropdown.
 *
 * `trailing` is the right-hand end of that same line — currently only Notes to Driver's character
 * count. One row rather than two stacked ones, so a field's hint and its counter read as a pair.
 */
const Field = ({
  field,
  error,
  hint,
  trailing,
  children,
}: {
  field: BookingField;
  error?: BulkRowError;
  /** Replaces "Optional" when a field needs to explain itself — currently only the disabled ones. */
  hint?: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) => {
  /*
   * One line under the control, never two: a problem always displaces "Optional".
   *
   * Optional sits below rather than beside the label so every field's label is the same shape, which
   * is what makes a two-column grid scan cleanly. It also leaves the slot free for the error.
   */
  const note = error ? (
    <Typography variant="small" className={clsx(error.level === "error" ? "text-danger" : "text-warning-dark")}>
      {error.message}
    </Typography>
  ) : hint ? (
    <Typography variant="paragraph" className="text-neutral-silver-gray">
      {hint}
    </Typography>
  ) : !field.required ? (
    <Typography variant="paragraph" className="text-neutral-silver-gray">
      Optional
    </Typography>
  ) : null;

  /*
   * The field is a NAMED GROUP, which is what makes it audible.
   *
   * Every label here is a Typography span with nothing tying it to its control, so a screen reader
   * announced fifteen anonymous "edit" boxes and left the user to guess which was the email and which
   * was the pickup date.
   *
   * A group rather than a `<label htmlFor>` because the controls are not all inputs: DropdownSelect and
   * DropdownCheckbox keep their real control inside a Headless UI wrapper, and LocationAddressAutocomplete
   * takes a closed prop list — none of them can be handed an id or an aria-label without changing a
   * shared atom, which is more than this ticket should be doing. Naming the group works for all fifteen
   * identically, and the error or hint is wired as the description so it is announced too rather than
   * being visual-only.
   */
  const labelId = `${useId()}-label`;
  const noteId = `${labelId}-note`;

  return (
    <div role="group" aria-labelledby={labelId} aria-describedby={note ? noteId : undefined}>
      <Typography id={labelId} variant="action" className="mb-1 block text-neutral-black">
        {field.label}
      </Typography>
      {children}
      {/* Only when there's something to put in it — a required field with no error and no counter
          keeps the spacing it had before this row existed. */}
      {(note || trailing) && (
        <div className="mt-1 flex items-start justify-between gap-2" id={noteId}>
          {/* Holds the left end even when there's no note, so `trailing` stays hard right. */}
          {note ?? <span />}
          {trailing}
        </div>
      )}
    </div>
  );
};

export type RowEdits = {
  values: Partial<Record<BookingFieldKey, string>>;
  /** Addresses picked from autocomplete — already resolved, so nothing needs looking up again. */
  resolved: Partial<Record<"pickup" | "destination", PointsOfInterest>>;
};

interface EditRowModalProps {
  open: boolean;
  onClose: () => void;
  rowNumber: number;
  values: Record<BookingFieldKey, string>;
  errors: BulkRowError[];
  /** The operator's transfer types, vehicles and extras — the same list the booking sheet offers. */
  bookingData?: ClientBookingData;
  /**
   * Hands the draft back and closes. Both callers save the same way: take the values, shut the modal,
   * and show the row as a skeleton while the server re-checks it.
   *
   * Deliberately NOT awaited here, and the modal has no pending state of its own. Holding it open
   * across a geocode and a validate call read as a hang — the only progress text lives in the
   * toolbar, behind this modal's own overlay.
   */
  onSave: (edits: RowEdits) => void;
}

/**
 * Fix one row's fields (DEV-1215).
 *
 * A modal rather than in-cell editors, deliberately. The review table is fourteen columns wide and
 * scrolls both ways, so anything that opens — a select, an address autocomplete — gets clipped by
 * the scroll container, and there's no room for labels or error text beside a cramped cell. Here
 * every field gets a proper control: real dropdowns of the operator's own options, and the same
 * address autocomplete the booking sheet uses.
 *
 * Picking an address returns a fully resolved place, so that row needs no further lookup — it's the
 * identical path a manual booking takes.
 */
export const EditRowModal = ({ open, onClose, rowNumber, values, errors, bookingData, onSave }: EditRowModalProps) => {
  const [draft, setDraft] = useState(values);
  const [resolved, setResolved] = useState<RowEdits["resolved"]>({});
  const [time, setTime] = useState(() => parsePickupTime(values.pickupTime ?? ""));

  /**
   * A form of exactly one field, purely so the booking sheet's own PhoneNumberInput can be reused
   * verbatim rather than reimplemented here.
   *
   * That component is built on `react-phone-number-input/react-hook-form` and takes `control` + `name`,
   * with no value/onChange variant — the same way PassengerPanel drives it. This modal keeps its draft
   * in plain state, so rather than cloning the country selector, the default-country lookup and the
   * styling into a second input that would immediately start drifting, it gets the form the component
   * expects. The draft stays the single source of truth; this only feeds it.
   */
  const defaultCountry = (useAppSelector(defaultCountrySelector) || "AU") as CountryCode;

  /*
   * Seeded from the RAW cell, deliberately. PhoneNumberInput reads the country off a leading "+", so a
   * spreadsheet's "0412345678" gives it nothing to select and the field looks empty — but seeding the
   * normalised form would mean opening a row and saving it silently rewrote the passenger's number,
   * which is the thing we decided not to do. The error beside it quotes the number instead.
   */
  const phoneForm = useForm<{ passengerPhone: string }>({
    defaultValues: { passengerPhone: values.passengerPhone ?? "" },
  });
  const phone = phoneForm.watch("passengerPhone");

  useEffect(() => {
    const next = phone ?? "";
    // Compared before writing: an unconditional set would re-render into this effect forever.
    setDraft((current) => (current.passengerPhone === next ? current : { ...current, passengerPhone: next }));
  }, [phone]);

  /** Keep the text value in the shape the backend parses: "6:00 PM". */
  const setTimePart = (part: "hour" | "minute" | "meridiem", value: string) => {
    const next = { ...time, [part]: value };
    setTime(next);
    set("pickupTime", next.hour ? `${next.hour}:${(next.minute || "00").padStart(2, "0")} ${next.meridiem}` : "");
  };

  /**
   * Snap the minute onto the 5-minute grid when the field loses focus.
   *
   * Copied from the booking sheet's own Date/Time step, which does exactly this — so a pickup you can
   * type here is a pickup you could have picked there. A spreadsheet cell holds 6:07 PM quite happily,
   * and until the server learned to refuse off-grid times, that row queued and failed at booking.
   *
   * On blur rather than on change, so typing "07" on the way to "07" → "05" isn't fought mid-keystroke.
   */
  /**
   * Fold a 24-hour hour into the 12-hour boxes when the field loses focus.
   *
   * Same reasoning as `parsePickupTime`, for a value typed rather than imported: 21 becomes 9 PM instead of
   * sitting there as "21 : 30 AM", which the server cannot parse at all. On blur, not on change, so
   * typing "1" on the way to "12" isn't rewritten mid-keystroke.
   */
  const snapHour = () => {
    const parsed = parseInt(time.hour, 10);
    if (Number.isNaN(parsed) || (parsed >= 1 && parsed <= 12)) return;

    const { hour, meridiem } = toTwelveHour(parsed % 24);
    const next = { ...time, hour: String(hour), meridiem };
    setTime(next);
    set("pickupTime", `${next.hour}:${(next.minute || "00").padStart(2, "0")} ${next.meridiem}`);
  };

  const snapMinute = () => {
    if (time.minute === "") return setTimePart("minute", "00");

    const parsed = parseInt(time.minute, 10);
    if (Number.isNaN(parsed)) return setTimePart("minute", "00");

    const snapped = Math.min(60 - STEP_MINUTES, Math.round(parsed / STEP_MINUTES) * STEP_MINUTES);
    setTimePart("minute", String(snapped).padStart(2, "0"));
  };

  const draftDate = parsePickupDate(draft.pickupDate ?? "");

  /**
   * The phone's real format check, run here rather than on the server.
   *
   * The sheet validates with libphonenumber in the browser and the PHP side has no equivalent, so the
   * server can only sanity-check the shape of a spreadsheet cell. A row opened here gets the genuine
   * article — the same `isValidPhoneNumber` the sheet's guest form refuses to submit without.
   *
   * Only once something has been typed: an empty field is the server's "required" message to give, and
   * shouting "invalid" at an untouched row would be noise.
   */
  /**
   * Live phone validation, held back until there is enough of a number to judge.
   *
   * Six digits, because the country selector pre-fills a calling code the moment the field is focused
   * — so "+61" alone is a field someone has started, not a number they got wrong, and shouting at it
   * meant the error appeared before the first digit was typed and stayed there while they typed.
   */
  const phoneDigits = (draft.passengerPhone ?? "").replace(/\D/g, "");
  const phoneEdited = (draft.passengerPhone ?? "") !== (values.passengerPhone ?? "");

  const phoneError: BulkRowError | undefined =
    phoneDigits.length >= 6 && !isValidPhoneNumber(draft.passengerPhone ?? "", defaultCountry)
      ? {
          field: "passengerPhone",
          level: "error",
          /*
           * Untouched, it quotes the ORIGINAL cell, because the input may not be able to display it —
           * the common cause is a spreadsheet eating the "+" off a foreign number, and "Invalid phone
           * number" beside a box that has given up leaves nothing to retype from.
           *
           * Once edited it must NOT quote that: the value on screen is the user's own, and repeating
           * the cell they are replacing reads as the field ignoring them. Worst when the column was
           * mis-mapped and the "number" is a URL — a line of address quoted back at someone typing a
           * phone number.
           */
          message: phoneEdited
            ? "That doesn't look like a valid phone number — check the country code and the digits."
            : `"${truncate(values.passengerPhone ?? "")}" is missing its country code — enter the number with its + prefix`,
        }
      : undefined;

  /**
   * The server's complaint about a field, dropped once that field has been changed.
   *
   * Every message here came back from the last validate call, so it describes the value the row
   * ARRIVED with. Leaving it up while someone corrects the field says the correction didn't take —
   * pick the right transfer type from the dropdown and the red border and "isn't one of your transfer
   * types" both stay put, which reads as the pick having been rejected.
   *
   * Cleared on the value differing rather than on the field being touched, so re-selecting the same
   * wrong option keeps its explanation. It is optimistic by nature: the row is only genuinely
   * re-checked on save, so a change from one bad value to another clears the message until then. That
   * is the right trade — the alternative is a message that is definitely stale, and the save re-checks
   * everything anyway.
   *
   * The phone keeps its own live check, which is a real validation running here rather than a
   * remembered one, so it survives the change that triggered it.
   */
  const errorFor = (field: BookingFieldKey) => {
    if (field === "passengerPhone" && phoneError) return phoneError;

    if ((draft[field] ?? "") !== (values[field] ?? "")) return undefined;

    return errors.find((error) => error.field === field && error.level === "error") ?? errors.find((error) => error.field === field);
  };

  /**
   * Does a flight number apply to this pickup? Mirrors the booking sheet's own gate.
   *
   * Read off the RESOLVED pickup when there is one, else the value the row arrived with — deliberately
   * not `draft.pickup`, which changes on every keystroke in the autocomplete. Keying on that would
   * disable and clear these two fields halfway through typing "Sydney Airport".
   */
  const flightApplies = isFlightNumberEnabled(resolved.pickup?.address ?? values.pickup, resolved.pickup?.rm_tags);

  /*
   * These two used to be disabled AND emptied on a non-airport pickup. The disabling stays; the
   * emptying is gone.
   *
   * Greying them out is deliberate (Jodie): the fields are irrelevant to a trip that doesn't start at
   * an airport, and leaving them live invites someone to fill one in the belief that it's wanted.
   *
   * Clearing them was the part that did harm, and none of that harm came from the greying. It
   * destroyed what the user had typed, so the same row imported differently depending on whether
   * anyone happened to open it — left alone it kept its flight number, opened and saved it lost one.
   *
   * Nothing is hidden from validation by disabling: the value stays in the draft, so it is still
   * submitted, still validated, and still carries the row's warning explaining that it won't be saved
   * and why. Disabled here means "not editable", not "not there" — and it needn't be editable, because
   * the server discards it regardless. A value typed against the wrong row is fixed by correcting the
   * pickup, which re-enables both fields with what was typed still in them.
   */

  const set = (field: BookingFieldKey, value: string) => setDraft((current) => ({ ...current, [field]: value }));

  const transferTypes = (bookingData?.transfer_types ?? []).map((type) => type.label);
  const vehicleTypes = (bookingData?.vehicles ?? []).map((vehicle) => vehicle.name);
  const extras = (bookingData?.extras ?? []).map((extra) => extra.name);

  /**
   * Extras are stored comma-separated, resolved to the operator's own spelling.
   *
   * Anything the operator doesn't offer is dropped rather than carried along invisibly: no checkbox
   * represents it, so it could never be unticked, and the server already flags it on the row
   * ("X isn't one of your extras and won't be added"). Deduped, since two spellings of one extra in
   * the sheet are still one extra.
   */
  const selectedExtras = useMemo(
    () => matchExtras(draft.extras ?? "", extras),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft.extras, extras.join("\u0000")]
  );

  const toggleExtra = (name: string) => {
    const next = selectedExtras.includes(name) ? selectedExtras.filter((extra) => extra !== name) : [...selectedExtras, name];
    set("extras", next.join(", "));
  };

  /**
   * The right-hand end of a field's hint line. Only Notes to Driver has one.
   *
   * Rendered here rather than by `TextArea` for LAYOUT, not correctness: the design puts this on the
   * same line as "Optional", hard right, and the atom's own counter sits inside its wrapper below the
   * field. The label text comes from the atom so the two can't word it differently.
   */
  /**
   * Fields still showing a blocking error — what stops the row being saved.
   *
   * Save used to go through regardless, so a row could be closed with six red fields still on it and
   * nothing said no. It came back refused a moment later, but by then the modal had shut with every
   * appearance of having accepted the changes.
   *
   * Counts only what is VISIBLE, which is what makes this fair: `errorFor` drops a field's message as
   * soon as its value changes, so correcting a field releases the button immediately rather than
   * holding it until the server has been asked. Warnings never block — they describe something the
   * server will accept.
   */
  const blockingFields = BOOKING_FIELDS.filter((field) => errorFor(field.key)?.level === "error");

  /**
   * Why a field is greyed out, in place of its "Optional". Only the flight pair has one.
   *
   * A warning outranks it in the note line, which is what makes the pair work: an EMPTY greyed field
   * explains why it's greyed, and one the row arrived with a value in shows the server's warning
   * saying that value won't be saved. Right message in each case, one line either way.
   */
  const hintFor = (key: BookingFieldKey) =>
    !flightApplies && (key === "flightNumber" || key === "arrivalDelay") ? "Only for airport pick-ups" : undefined;

  const trailingFor = (key: BookingFieldKey) => {
    if (key !== "notesToDriver") return undefined;

    return (
      <Typography variant="small" className="shrink-0 text-neutral-dark-gray">
        {charactersLeftLabel(NOTES_MAX_LENGTH, draft.notesToDriver?.length ?? 0)}
      </Typography>
    );
  };

  const renderControl = (key: BookingFieldKey) => {
    const hasError = errorFor(key)?.level === "error";

    // Addresses get the booking sheet's own picker, so a chosen place arrives fully resolved and
    // needs no further lookup.
    if (key === "pickup" || key === "destination") {
      return (
        <LocationAddressAutocomplete
          value={draft[key] ?? ""}
          onChange={(text) => set(key, text)}
          onSelect={(poi) => {
            set(key, poi.address);
            setResolved((current) => ({ ...current, [key]: poi }));
          }}
          includeFavourites
          placeholder="Search or type an address"
        />
      );
    }

    if (key === "transferType" || key === "vehicleType") {
      const options = key === "transferType" ? transferTypes : vehicleTypes;
      return (
        <DropdownSelect
          size="md"
          options={options}
          value={canonical(draft[key] ?? "", options)}
          placeholder={options.length ? "Select" : "Loading…"}
          hasError={hasError}
          onChange={(value: string) => set(key, value)}
        />
      );
    }

    if (key === "pickupDate") {
      // Same react-datepicker the booking sheet uses, so a date can't be typed into an ambiguous
      // format — the backend still receives DD/MM/YYYY.
      return (
        <DateInput
          selected={draftDate}
          onChange={(date) => set(key, date ? format(date, DATE_FORMAT) : "")}
          hasError={hasError}
          minDate={new Date()}
          /*
           * Shown as DD/MM/YYYY, not the atom's default "30 Aug 2026".
           *
           * The value written has always been DD/MM/YYYY — the template documents it and the server's
           * own error names it — so the picker displaying a different shape made the field look like
           * it disagreed with the instruction directly above it.
           */
          dateFormat={DATE_FORMAT}
          placeholderText="DD/MM/YYYY"
        />
      );
    }

    if (key === "passengerPhone") {
      // The sheet's input, unchanged: country selector, operator default country, length limiting.
      return <PhoneNumberInput control={phoneForm.control} name="passengerPhone" size="md" hasError={hasError} />;
    }

    if (key === "passengerTitle") {
      return (
        <DropdownSelect
          size="md"
          className="w-full"
          options={TITLE_OPTIONS}
          value={draft.passengerTitle ?? ""}
          onChange={(value: string) => set("passengerTitle", value)}
          placeholder="Select Title"
          hasError={hasError}
        />
      );
    }

    if (key === "pickupTime") {
      // Hour / minute / meridiem, mirroring the sheet's Date and Time step.
      return (
        <div className="flex items-center gap-2">
          <TextInput
            size="md"
            className="w-16"
            value={time.hour}
            onChange={(event) => setTimePart("hour", event.target.value.replace(/\D/g, "").slice(0, 2))}
            onBlur={snapHour}
            hasError={hasError}
            placeholder="6"
            aria-label="Hour"
          />
          <Typography className="text-neutral-dark-gray">:</Typography>
          <TextInput
            size="md"
            className="w-16"
            value={time.minute}
            onChange={(event) => setTimePart("minute", event.target.value.replace(/\D/g, "").slice(0, 2))}
            onBlur={snapMinute}
            hasError={hasError}
            placeholder="00"
            aria-label="Minute"
          />
          <DropdownSelect
            size="md"
            className="w-24"
            options={MERIDIEMS}
            value={time.meridiem}
            onChange={(value: string) => setTimePart("meridiem", value)}
          />
        </div>
      );
    }

    if (key === "extras") {
      if (extras.length === 0) return <Typography className="text-neutral-silver-gray">No extras configured</Typography>;

      // Same control the sheet's Vehicle and Transfer step uses, down to the height override — the
      // atom is hardcoded min-h-[48px] with no size prop.
      return (
        <DropdownCheckbox
          className="w-full [&>button>span]:min-w-0 [&>button]:!flex [&>button]:!h-[38px] [&>button]:!min-h-0 [&>button]:!items-center"
          placeholder="Select Extras"
          selectedCount={selectedExtras.length}
          getSelectedLabel={() => (selectedExtras.length ? selectedExtras.join(", ") : "Select Extras")}
        >
          {extras.map((name) => (
            <CheckboxInput
              key={name}
              variant="check"
              label={name}
              checked={selectedExtras.includes(name)}
              onChange={() => toggleExtra(name)}
            />
          ))}
        </DropdownCheckbox>
      );
    }

    if (key === "notesToDriver") {
      // The atom's own count is suppressed: it renders on a line of its own below the field, where
      // this design wants it opposite "Optional" on one row. `trailingFor` draws it instead.
      return (
        <TextArea
          rows={3}
          maxLength={NOTES_MAX_LENGTH}
          hideCharCount
          value={draft[key] ?? ""}
          onChange={(event) => set(key, event.target.value)}
          hasError={hasError}
        />
      );
    }

    /*
     * Both flight fields are greyed out unless the pickup is an airport or terminal — they are
     * irrelevant to a trip that doesn't start at one, and a live field invites someone to fill it.
     *
     * Greyed, NOT emptied: whatever the row arrived with stays visible, so the user can see what they
     * typed and which row they meant it for, and the value still goes through validation. What that
     * value does is the server's call — it discards a flight number on a departure and ignores a delay
     * with no landing to time against, and warns about both.
     */
    if (key === "flightNumber" || key === "arrivalDelay") {
      return (
        <TextInput
          size="md"
          value={draft[key] ?? ""}
          onChange={(event) => set(key, event.target.value)}
          hasError={hasError}
          disabled={!flightApplies}
          // `Field` draws its label as a Typography span with nothing tying it to the control, so
          // every input here is unlabelled to a screen reader. Fixed for these two because they are
          // the ones being changed; the rest is a wider job than this ticket.
          aria-label={fieldOf(key).label}
        />
      );
    }

    // Counts stay plain numeric text inputs. Capacity is still enforced server-side, which reports it
    // as a row error rather than silently clamping.
    if (key === "passengerCount" || key === "bagCount") {
      return (
        <TextInput
          size="md"
          inputMode="numeric"
          value={draft[key] ?? ""}
          onChange={(event) => set(key, event.target.value.replace(/\D/g, ""))}
          hasError={hasError}
        />
      );
    }

    return <TextInput size="md" value={draft[key] ?? ""} onChange={(event) => set(key, event.target.value)} hasError={hasError} />;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="web-medium"
      title={`Edit booking ${rowNumber}`}
      description="Fix anything flagged below. The booking is re-checked and re-priced when you save."
      renderButtons={() => (
        <>
          <Button variant="tertiary" size="md" onClick={onClose}>
            Cancel
          </Button>
          {/* Tippy binds to the child and a disabled <button> emits no pointer events, so the tooltip
              hangs off a wrapping span — the same trick the wizard's own gated buttons use. */}
          <Tooltip
            disabled={blockingFields.length === 0}
            content={`Fix ${blockingFields.length === 1 ? "this field" : `these ${blockingFields.length} fields`} first: ${blockingFields
              .map((field) => field.label)
              .join(", ")}.`}
          >
            <span className="inline-flex">
              <Button size="md" disabled={blockingFields.length > 0} onClick={() => onSave({ values: draft, resolved })}>
                Save Changes
              </Button>
            </span>
          </Tooltip>
        </>
      )}
    >
      <div className="flex max-h-[55vh] flex-col gap-3 overflow-y-auto pr-1">
        {SECTIONS.map((section) => (
          <Section key={section.title} icon={section.icon} title={section.title}>
            <div className="flex flex-col gap-4">
              {section.rows.map((row, index) => (
                <div key={index} className={clsx("grid grid-cols-1 gap-4", row.length > 1 && "sm:grid-cols-2")}>
                  {row.map((key) => (
                    <Field key={key} field={fieldOf(key)} error={errorFor(key)} hint={hintFor(key)} trailing={trailingFor(key)}>
                      {renderControl(key)}
                    </Field>
                  ))}
                </div>
              ))}
            </div>
          </Section>
        ))}
      </div>
    </Modal>
  );
};
